import reduxFreeFetcher from '@api/fetchers';
import simpleFetch from '@api/fetchers/simpleFetcher';
import { getCMSHost } from '@utils/api-helper/host';
import { readCookie } from '@utils/cookies';
import type {
  CampaignScreen,
  CampaignState,
  CMSCampaignResponse,
  CMSPriceCard,
  CMSReviewCard,
  PollOption,
  PrizeCard,
  PrizeKey,
  Quote,
  LeaderboardEntry,
  ReferralInvite,
  ReferralInvitesResponse,
  ReferralSquadData,
  SquadMember
} from './types';
import resolveRevealEndsAt from './revealCountdown';
import { REFERRAL_PROMOTIONS_CMS_PATH } from './feature';

const REFERRAL_INVITES_ENDPOINT = 'api/v1/subscriptions/referral_invites';

const SCREEN_TYPE_MAP: Record<number, CampaignScreen> = {
  1: 'poll',
  2: 'squad',
  3: 'reveal'
};

export class CampaignInvalidScreenError extends Error {
  constructor() {
    super('Unknown campaign screen');
    this.name = 'CampaignInvalidScreenError';
  }
}

export const isCampaignScreenTypeActive = (screenType: number): boolean =>
  Boolean(SCREEN_TYPE_MAP[Number(screenType)]);

export const fetchCampaignCms = async (): Promise<CMSCampaignResponse> => {
  const cmsHost = (getCMSHost() || 'https://cms.unacademy.com/').replace(
    /\/?$/,
    '/'
  );
  return (await simpleFetch(
    `${cmsHost}${REFERRAL_PROMOTIONS_CMS_PATH}`
  )) as CMSCampaignResponse;
};

const inferPrizeKey = (title: string): PrizeKey | undefined => {
  const normalized = title.toLowerCase();
  if (normalized.includes('hunter')) return 'hunter';
  if (normalized.includes('playstation') || normalized.includes('ps5'))
    return 'ps5';
  if (normalized.includes('euro')) return 'euro';
  if (normalized.includes('mac')) return 'macbook';
  return undefined;
};

const mapQuotes = (cards: CMSReviewCard[]): Quote[] =>
  cards.map((card) => ({
    id: String(card.id),
    text: card.reviewText,
    name: card.reviewerName,
    tag: card.reviewerGoal.trim(),
    avatarUrl: card.reviewerAvatar.url
  }));

const mapPollOptions = (cards: CMSPriceCard[]): PollOption[] =>
  cards.map((card) => ({
    id: String(card.id),
    prizeKey: inferPrizeKey(card.titleText),
    title: card.titleText.trim(),
    subtitle: card.subTitle,
    percent: card.percentage,
    imageUrl: card.priceImage.url
  }));

const mapPrizes = (cards: CMSPriceCard[]): PrizeCard[] =>
  cards.map((card) => ({
    id: String(card.id),
    prizeKey: inferPrizeKey(card.titleText),
    title: card.titleText.trim(),
    subtitle: card.subTitle,
    imageUrl: card.priceImage.url
  }));

const maskPhone = (phone: string) => {
  const digits = (phone || '').replace(/\D/g, '').slice(-10);
  if (digits.length < 4) return 'xxxx';
  return `${digits.slice(0, 4)}xxx`;
};

export const mapInviteToMember = (
  invite: ReferralInvite,
  index: number
): SquadMember => ({
  id: invite.uid || `invite-${index}`,
  label: maskPhone(invite.phone),
  avatarIndex: (index % 9) + 1,
  phone: (invite.phone || '').replace(/\D/g, '').slice(-10)
});

const EMPTY_SQUAD_DATA: ReferralSquadData = {
  members: [],
  extraCount: 0,
  leaderboard: []
};

const mapLeaderboard = (
  items?: ReferralInvitesResponse['leaderboard']
): LeaderboardEntry[] =>
  (items || [])
    .slice()
    .sort((a, b) => (b.referredCount || 0) - (a.referredCount || 0))
    .slice(0, 5)
    .map((item, index) => ({
      id: String(item.userId ?? item.name ?? index),
      rank: index + 1,
      name: item.name || '',
      tag: (item.goal || '').trim(),
      friends: item.referredCount || 0,
      avatarIndex: (index % 9) + 1
    }));

export const fetchReferralSquad = async (): Promise<ReferralSquadData> => {
  if (typeof window !== 'undefined' && !readCookie('accessToken')) {
    return EMPTY_SQUAD_DATA;
  }

  const response = await reduxFreeFetcher(REFERRAL_INVITES_ENDPOINT, {
    skipLoader: true,
    useOriginalURL: true,
    useAuthToken: true
  });
  if (!response || response.error) return EMPTY_SQUAD_DATA;

  const payload = response as ReferralInvitesResponse;
  const members = (payload.squad || []).map(mapInviteToMember);

  return {
    members,
    extraCount: Math.max(0, members.length - 9),
    leaderboard: mapLeaderboard(payload.leaderboard)
  };
};

export const inviteReferralBuddy = async (payload: {
  phone: string;
  name?: string;
}): Promise<ReferralInvite | null> => {
  const response = await reduxFreeFetcher(REFERRAL_INVITES_ENDPOINT, {
    requestType: 'POST',
    skipLoader: true,
    useAuthToken: true,
    useOriginalURL: true,
    body: {
      phone: payload.phone,
      name: payload.name || ''
    }
  });

  if (!response || response.error) return null;
  return response as ReferralInvite;
};

export const fetchCampaignState = async (): Promise<CampaignState> => {
  const response = await fetchCampaignCms();

  const mappedScreen = SCREEN_TYPE_MAP[Number(response.screenType)];
  if (!mappedScreen) {
    throw new CampaignInvalidScreenError();
  }

  if (response.priceCard.length === 0 || response.reviewCards.length === 0) {
    throw new Error('Invalid campaign CMS payload');
  }

  const quotes = mapQuotes(response.reviewCards);
  const options = mapPollOptions(response.priceCard);
  const prizes = mapPrizes(response.priceCard);
  const squadData = await fetchReferralSquad();

  return {
    screen: mappedScreen,
    poll: {
      options,
      selectedOptionId: null,
      quotes
    },
    squad: {
      ...squadData,
      quotes
    },
    reveal: {
      prizes,
      mysteryPrizes: [],
      endsAt:
        mappedScreen === 'reveal' ? resolveRevealEndsAt(response) : undefined
    }
  };
};
